<template>
  <div class="login">
    <div class="login-box">
      <div class="login-logo-max">
        <img src="../../assets/img/login-logo.png" alt="Ticketing Logo" />
      </div>

      <h1 class="login-title">{{ $locale("login.headline") }}</h1>
      <el-form
        :label-position="labelPosition"
        :model="form"
        class="login-from"
        :rules="loginFromRules"
        @keyup.enter.native="onSubmitFn"
        @submit="onSubmitFn"
      >
        <el-form-item prop="userName">
          <el-input
            :placeholder="$locale('form.plsEnterYourUserName')"
            prefix-icon="el-icon-user"
            v-model.trim="form.userName"
            maxlength="50"
          ></el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            :placeholder="$locale('form.plsEnterYourPassword')"
            prefix-icon="el-icon-lock"
            show-password
            v-model.trim="form.password"
            maxlength="50"
          ></el-input>
        </el-form-item>
        <el-form-item class="submit-item">
          <el-button
            @click="onSubmitFn"
            :class="isSubmitDisabled ? 'egl-btn-gray' : 'egl-btn-yellow'"
            :disabled="isSubmitDisabled"
          >
            {{ $locale("global.login") }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-dialog
      :visible="pwdOverdue"
      class="egl-dialog"
      :title="$locale('login.passwordHasExpired')"
      :close-on-click-modal="false"
      :modal="false"
      @close="closeResetPwDialog"
    >
      <el-form
        label-position="top"
        :model="updatePwd"
        :rules="resetPWFromRules"
        @keyup.enter.native="resetPwd"
        @submit="resetPwd"
        ref="restPasswordForm"
      >
        <el-row>
          <el-col :span="24">
            <el-form-item :label="$locale('login.oldPassword')" prop="oldPassword">
              <el-input
                type="password"
                v-model.trim="updatePwd.oldPassword"
                :placeholder="$locale('login.plsEntertOldPassword')"
                maxlength="50"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$locale('login.newPassword')" prop="newPassword">
              <el-input
                type="password"
                v-model.trim="updatePwd.newPassword"
                :placeholder="$locale('login.plsEntertNewPassword')"
                maxlength="50"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col>
            <el-form-item :label="$locale('login.confirmNewPassword')" prop="passwordAgain">
              <el-input
                type="password"
                v-model.trim="updatePwd.passwordAgain"
                :placeholder="$locale('login.plsEnterConfirmNewPassword')"
                maxlength="50"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-row :gutter="20" class="reset-password-dialog-footer">
          <el-button class="egl-btn-white" @click="closeResetPwDialog">{{$locale("global.cancel")}}</el-button>
          <el-button class="egl-btn-yellow" @click="resetPwd">{{$locale("global.confirm")}}</el-button>
        </el-row>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/**
 * Removed the comment codes from the old  version,
 * you can get a reference for the old version with file in this path: "views/login.vue"
 */
import { mapActions, mapState } from "vuex";
import { MessageBox } from "element-ui";
import { localStorage } from "@/utils/localStorage";
import { setLocalStorage } from '@/assets/js/storage';
import {
  getSystemType,
  getCinemaList,
  resetPassword,
  loginService,
  getSystemSetting,
  getAccountDetailService,
} from "@/helpers/service/api/common.js";
import { requestAll } from "@/helpers/service/axiosPlugin";
import { clearBrowserData } from "@/utils/common";
import "@/assets/style/login.less";

export default {
  name: "login",
  data() {
    return {
      isLoading: false,
      fromPageName: "/dashboard",
      labelPosition: "right",
      form: {
        userName: "",
        password: ""
      },
      updatePwd: {
        oldPassword: "",
        newPassword: "",
        passwordAgain: "",
      },
      pwdOverdue: false,
      loginFromRules: {
        userName: [ {
          required: true,
          message: this.$locale("form.plsEnterYourUsername"),
          trigger: "blur",
        } ],
        password: [ {
          required: true,
          message: this.$locale("form.plsEnterYourPassword"),
          trigger: "blur",
        } ]
      },
      resetPWFromRules: {
        oldPassword: [ {
          required: true,
          message: this.$locale("login.plsEntertOldPassword"),
          trigger: "blur",
        } ],
        newPassword: [ {
          required: true,
          message: this.$locale("login.plsEntertNewPassword"),
          trigger: "blur",
        } ],
        passwordAgain: [
          {
            required: true,
            message: this.$locale("login.plsEnterConfirmNewPassword"),
            trigger: "blur",
          },
          {
            validator: this.passwordMatchValidator,
            trigger: "blur",
          }
        ],
      },
      errMapping: {
        AUTH_FAIL: "auth_fail",
        PW_EXPIRED: "password_expired",
        ACC_INACTIVATED: "account_inactivated",
      },
    };
  },
  computed: {
    ...mapState("app", {
      token: state => state.token,
      permissionMap: state => state.permissionMap,
    }),
    isSubmitDisabled() {
      return Boolean(Object.keys(this.form).find(key => this.form[key] === "") || this.isLoading);
    },
  },
  // beforeRouteEnter(to, from, next) {
  //   next(vm => {
  //     vm.fromPageName = from.name ? from.name : "/dashboard";
  //   });
  // },
  methods: {
    ...mapActions("app", [
      "setAppData",
      "logoutAction",
    ]),
    onSubmitFn() {
      this.isLoading = true;
      clearBrowserData();

      loginService({
        ...this.form,
        platform: "webconsole",
      }).then(res => {
        const {
          id, userName, platformTokens,
          moduleMap, permissionMap,
          type, admin,
          cinemas, brandId,
        } = res.data;
        setLocalStorage("adminType", type);
        const bearerToken = "Bearer " + platformTokens.webconsole;
        localStorage.set("token", bearerToken);

        let originalUserMsg = {
          token: bearerToken,
          userName,
          cinemas,
          cinemaId: type === "cinema" ? cinemas[0] : "",
          brandId,
          accountId: id,
          moduleMap,
          permissionMap,
          adminType: type,
          isAdmin: admin,
        };

        /** Get brand setting / account detail info */
        requestAll([
          getSystemSetting(),
          getAccountDetailService(id),
        ]).then(res => {
          const { round, brandCode } = res[0].data;
          const accountDetailInfo = res[1].data;

          originalUserMsg = {
            ...originalUserMsg,
            accountDetailInfo,
            round,
            brandCode,
          };

          this.setAppData({ data: originalUserMsg, isLogin: true });

          this.getCinemaAndType(permissionMap, type);
        });
      }).catch(err => {
        this.logoutAction();
        this.isLoading = false;

        const errMsg = err.data.error;

        if (errMsg) {
          switch(errMsg) {
          case this.errMapping.AUTH_FAIL:
            MessageBox(this.$locale("err.usernamePwNotMatch"));
            break;

          case this.errMapping.PW_EXPIRED:
            this.pwdOverdue = true;
            break;

          case this.errMapping.ACC_INACTIVATED:
            MessageBox(this.$locale("login.accountInactivated"));
            break;

          default:
            MessageBox(err.data.message || errMsg);
            break;
          }
        }
      });
    },
    getType_fn() {
      return getSystemType({}, false, false);
    },
    getCinema_fn() {
      return getCinemaList({ page: 0, pageSize: 9999 }, false, false);
    },
    getCinemaAndType(permissionMap, type) {
      const that = this;

      if (permissionMap.cinema && permissionMap.cinema.types.indexOf("view") >= 0) {
        requestAll([that.getType_fn(), that.getCinema_fn()]).then(res => {
          this.isLoading = false;

          let cinema_arr = [];
          const cinemaList = res[1].data.data;

          if (cinemaList.length > 0) {
            cinemaList.map(item => {
              cinema_arr.push({
                id: item.id,
                name: item.name,
                address1: item.address1,
                address2: item.address2,
                brandId: item.brandId,
              });
            });
          }

          const saveData = {
            dictionary: res[0].data,
            cinemaObj: cinema_arr,
            cinemaId: type === "cinema" ? cinema_arr[0].id : "",
          };

          this.setAppData({ data: saveData, isSupplement: true });
          this.$router.push(this.fromPageName);
        });
      } else {
        requestAll([ that.getType_fn() ]).then(res => {
          let cinema_arr = [];

          const saveData = {
            dictionary: res[0].data,
            cinemaObj: cinema_arr,
          };

          this.setAppData({ data: saveData, isSupplement: true });
          this.$router.push(this.fromPageName);
        });
      }
    },
    resetPwd() {
      this.$refs.restPasswordForm.validate(valid => {
        if (valid) {
          resetPassword({
            userName: this.form.userName,
            ...this.updatePwd,
          }).then(() => {
            this.closeResetPwDialog();
            this.$alert(this.$locale("global.updateSuccessfully"), {
              showClose: false
            });
          });
        } else {
          setTimeout(() => {
            const isError = document.getElementsByClassName("is-error");
            isError[0].querySelector("input").focus();
          }, 100);

          return false;
        }
      });
    },
    closeResetPwDialog() {
      this.$refs.restPasswordForm.clearValidate();
      this.$refs.restPasswordForm.resetFields();
      this.pwdOverdue = false;
    },
    passwordMatchValidator(_, value, callback) {
      const that = this;

      if (value !== "") {
        if (value !== that.updatePwd.newPassword) {
          callback(new Error(that.$locale("login.newPasswordDifferent")));
        } else if (value === that.updatePwd.oldPassword) {
          callback(new Error(that.$locale("login.newPasswordCanNotBeTheSame")));
        } else {
          callback();
        }
      } else {
        callback();
      }
    },
  },
};
</script>

<style scoped lang="less">
  .reset-password-dialog-footer {
    text-align: center;

    > .el-button {
      min-width: 200px;
    }
  }
</style>
